/**
 * Design tokens for Recipe Designer
 * Based on QFlow design language with custom refinements for this feature
 */

// Color palette
export const colors = {
    // Primary backgrounds
    background: {
        panel: '#FFFFFF',
        canvas: '#FAFBFC',
        elevated: '#FFFFFF',
        subtle: 'rgba(0, 0, 0, 0.01)',
        highlight: 'rgba(77, 166, 133, 0.03)',
    },

    // Borders
    border: {
        subtle: 'rgba(0, 0, 0, 0.06)',
        default: 'rgba(0, 0, 0, 0.08)',
        hover: 'rgba(0, 0, 0, 0.12)',
        active: 'rgba(0, 0, 0, 0.15)',
        focus: '#4DA685',
        error: 'rgba(244, 118, 96, 0.3)',
        errorStrong: 'rgba(244, 118, 96, 0.5)',
    },

    // Text
    text: {
        primary: '#272931',
        secondary: 'rgba(39, 41, 49, 0.7)',
        tertiary: 'rgba(39, 41, 49, 0.5)',
        disabled: 'rgba(39, 41, 49, 0.3)',
        inverse: '#FFFFFF',
    },

    // Brand/Accent - Primary (Green)
    accent: {
        primary: '#4DA685',
        primaryHover: '#3D8B6C',
        primaryActive: '#2F6E54',
        primarySubtle: 'rgba(77, 166, 133, 0.04)',
        primaryLight: 'rgba(77, 166, 133, 0.08)',
        primaryBorder: 'rgba(77, 166, 133, 0.15)',
        primaryBorderStrong: 'rgba(77, 166, 133, 0.3)',
        primaryBorderLight: 'rgba(77, 166, 133, 0.1)',
    },

    // Secondary (Blue - for tool indicators)
    secondary: {
        main: '#2563eb',
        subtle: 'rgba(37, 99, 235, 0.08)',
        border: 'rgba(37, 99, 235, 0.2)',
    },

    // Error (Red) - Enhanced hierarchy
    error: {
        main: '#DC2626',           // Darker, more sophisticated red
        mainLegacy: '#f47660',     // Original color for backwards compatibility
        text: '#B91C1C',           // Dark red for text on light backgrounds
        accent: '#EF4444',         // Bright red for icons/accents

        // Executive-level (most prominent)
        executive: {
            bg: 'linear-gradient(135deg, rgba(244, 118, 96, 0.04) 0%, rgba(244, 118, 96, 0.08) 100%)',
            border: '#DC2626',
            shadow: '0 2px 8px rgba(244, 118, 96, 0.12)',
        },

        // Detail-level (moderate prominence)
        detail: {
            bg: '#FFFBFA',         // Very subtle warm tint
            border: 'rgba(244, 118, 96, 0.12)',
            borderStrong: 'rgba(244, 118, 96, 0.3)',
        },

        // Step-level (least prominent)
        step: {
            bg: '#FFFCFC',         // Very subtle red tint
            border: 'rgba(244, 118, 96, 0.3)',
            borderDashed: '1px dashed rgba(244, 118, 96, 0.3)',
        },

        // Legacy (maintain backwards compatibility)
        subtle: 'rgba(244, 118, 96, 0.1)',
        border: 'rgba(244, 118, 96, 0.3)',
    },

    // Warning (Amber)
    warning: {
        main: '#F59E0B',
        subtle: 'rgba(245, 158, 11, 0.1)',
        border: 'rgba(245, 158, 11, 0.3)',
    },

    // Success (Green)
    success: {
        main: '#4DA685',
        subtle: 'rgba(77, 166, 133, 0.1)',
        border: 'rgba(77, 166, 133, 0.3)',
    },

    // Step connector
    connector: 'rgba(77, 166, 133, 0.2)',
};

// Shadows (minimal, prefer borders)
export const shadows = {
    sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 2px 8px rgba(0, 0, 0, 0.04)',
    lg: '0 4px 12px rgba(0, 0, 0, 0.06)',
    button: '0 4px 12px rgba(77, 166, 133, 0.15)',
    buttonActive: '0 2px 8px rgba(77, 166, 133, 0.12)',
    card: '0 4px 12px rgba(0, 0, 0, 0.04)',
    dialog: '0 20px 40px rgba(0, 0, 0, 0.08)',
};

// Border radius
export const radius = {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '10px',
};

// Spacing (based on 4px grid)
export const spacing = {
    xs: '4px',    // 0.25rem
    sm: '8px',    // 0.5rem
    md: '12px',   // 0.75rem
    lg: '16px',   // 1rem
    xl: '20px',   // 1.25rem
    xxl: '24px',  // 1.5rem
    xxxl: '32px', // 2rem
};

// Typography scale
export const typography = {
    // Panel title (uppercase label)
    panelTitle: {
        fontSize: '0.6875rem', // 11px
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: colors.text.tertiary,
    },

    // Section title (uppercase)
    sectionTitle: {
        fontSize: '0.75rem', // 12px
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: colors.text.tertiary,
    },

    // Recipe name (prominent)
    recipeName: {
        fontSize: '1.125rem', // 18px
        fontWeight: 500,
        letterSpacing: '-0.01em',
        color: colors.text.primary,
    },

    // Step label
    stepLabel: {
        fontSize: '0.9375rem', // 15px
        fontWeight: 500,
        letterSpacing: '-0.01em',
        color: colors.text.primary,
    },

    // Form label
    formLabel: {
        fontSize: '0.8125rem', // 13px
        fontWeight: 500,
        color: colors.text.secondary,
    },

    // Body text
    body: {
        fontSize: '0.875rem', // 14px
        fontWeight: 400,
        letterSpacing: '-0.01em',
        color: colors.text.primary,
    },

    // Small body text
    bodySmall: {
        fontSize: '0.8125rem', // 13px
        fontWeight: 400,
        letterSpacing: '-0.01em',
        color: colors.text.tertiary,
    },

    // Helper text
    helper: {
        fontSize: '0.75rem', // 12px
        fontWeight: 400,
        color: colors.text.tertiary,
    },

    // Badge/Tag
    badge: {
        fontSize: '0.6875rem', // 11px
        fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
    },
};

// Transitions
export const transitions = {
    // Fast, snappy interactions
    fast: '0.15s ease',

    // Standard transitions
    default: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',

    // Smooth, deliberate animations
    smooth: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',

    // Spring-like bounce (for expand/collapse)
    spring: '0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
};

// Panel dimensions
export const panelDimensions = {
    leftWidth: 280,
    rightWidth: 320,
    minCenterWidth: 500,
};

// Mobile-specific dimensions
export const mobileDimensions = {
    bottomNavHeight: 56,
    headerHeight: 64,
};

// Mobile spacing (reduced for smaller screens)
export const mobileSpacing = {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    xxl: '20px',
    xxxl: '24px',
};

// Z-index layers
export const zIndex = {
    panel: 1,
    header: 10,
    dropdown: 100,
    modal: 1000,
    tooltip: 1100,
};
